import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import analyticsService from '../../services/analyticsService';
import conversionTrackingService from '../../services/conversionTrackingService';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, Target, Activity, DollarSign, Eye, Clock } from 'lucide-react';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d');
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    totalConversions: 0,
    conversionRate: 0,
    totalRevenue: 0,
    avgSessionDuration: 0,
    pageViews: 0,
  });
  const [conversionData, setConversionData] = useState([]);
  const [funnelData, setFunnelData] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [userFlowData, setUserFlowData] = useState([]);

  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange(dateRange);

      // Load conversion metrics
      const registrations = await conversionTrackingService?.getConversionMetrics('registration', startDate, endDate);
      const applications = await conversionTrackingService?.getConversionMetrics('job_application', startDate, endDate);
      const subscriptions = await conversionTrackingService?.getConversionMetrics('subscription', startDate, endDate);

      // Load funnel data
      const regFunnel = await conversionTrackingService?.getFunnelMetrics('registration', startDate, endDate);
      const appFunnel = await conversionTrackingService?.getFunnelMetrics('job_application', startDate, endDate);
      const subFunnel = await conversionTrackingService?.getFunnelMetrics('subscription', startDate, endDate);

      // Load page analytics
      const { data: pageAnalytics } = await supabase?.from('page_analytics')?.select('*')?.gte('created_at', startDate)?.lte('created_at', endDate)?.order('view_count', { ascending: false })?.limit(10);

      // Load user flow data
      const { data: userFlows } = await supabase?.from('user_flow_events')?.select('*')?.gte('created_at', startDate)?.lte('created_at', endDate)?.order('created_at', { ascending: true });

      // Calculate metrics
      const totalConversions = (registrations?.total || 0) + (applications?.total || 0) + (subscriptions?.total || 0);
      const totalRevenue = subscriptions?.totalValue || 0;

      setMetrics({
        totalUsers: registrations?.total || 0,
        totalConversions: totalConversions,
        conversionRate: registrations?.total > 0 ? ((totalConversions / registrations?.total) * 100)?.toFixed(2) : 0,
        totalRevenue: totalRevenue,
        avgSessionDuration: calculateAvgSessionDuration(userFlows),
        pageViews: pageAnalytics?.reduce((sum, page) => sum + (page?.view_count || 0), 0) || 0,
      });

      // Set conversion data for chart
      setConversionData([
        { name: 'Registrations', value: registrations?.total || 0, color: COLORS?.[0] },
        { name: 'Applications', value: applications?.total || 0, color: COLORS?.[1] },
        { name: 'Subscriptions', value: subscriptions?.total || 0, color: COLORS?.[2] },
      ]);

      // Set funnel data
      setFunnelData([
        { name: 'Registration', steps: regFunnel?.stepCounts || {} },
        { name: 'Application', steps: appFunnel?.stepCounts || {} },
        { name: 'Subscription', steps: subFunnel?.stepCounts || {} },
      ]);

      // Set top pages
      setTopPages(pageAnalytics || []);

      // Process user flow data
      setUserFlowData(processUserFlowData(userFlows));

    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = (range) => {
    const endDate = new Date()?.toISOString();
    const startDate = new Date();

    switch (range) {
      case '24h':
        startDate?.setHours(startDate?.getHours() - 24);
        break;
      case '7d':
        startDate?.setDate(startDate?.getDate() - 7);
        break;
      case '30d':
        startDate?.setDate(startDate?.getDate() - 30);
        break;
      case '90d':
        startDate?.setDate(startDate?.getDate() - 90);
        break;
      default:
        startDate?.setDate(startDate?.getDate() - 7);
    }

    return { startDate: startDate?.toISOString(), endDate };
  };

  const calculateAvgSessionDuration = (flows) => {
    if (!flows || flows?.length === 0) return 0;
    const sessions = {};
    flows?.forEach(flow => {
      if (!sessions?.[flow?.session_id]) {
        sessions[flow.session_id] = { start: flow?.created_at, end: flow?.created_at };
      } else {
        sessions[flow.session_id].end = flow?.created_at;
      }
    });

    const durations = Object.values(sessions)?.map(session => {
      return (new Date(session.end) - new Date(session.start)) / 1000;
    });

    return durations?.length > 0 ? (durations?.reduce((a, b) => a + b, 0) / durations?.length)?.toFixed(0) : 0;
  };

  const processUserFlowData = (flows) => {
    if (!flows || flows?.length === 0) return [];
    const flowMap = {};
    flows?.forEach(flow => {
      const key = `${flow?.from_page} → ${flow?.to_page}`;
      flowMap[key] = (flowMap?.[key] || 0) + 1;
    });

    return Object.entries(flowMap)?.map(([flow, count]) => ({ flow, count }))?.sort((a, b) => b?.count - a?.count)?.slice(0, 10);
  };

  const exportData = () => {
    const dataToExport = {
      metrics,
      conversions: conversionData,
      funnels: funnelData,
      topPages,
      userFlows: userFlowData,
      exportedAt: new Date()?.toISOString(),
    };

    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-report-${dateRange}-${Date.now()}.json`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Analytics Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400">Comprehensive insights into user behavior and platform performance</p>
        </div>

        {/* Controls */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Date Range:</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </select>
          </div>
          <button
            onClick={exportData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Export Data
          </button>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <MetricCard
            icon={<Users className="w-6 h-6" />}
            title="Total Users"
            value={metrics?.totalUsers}
            color="blue"
          />
          <MetricCard
            icon={<Target className="w-6 h-6" />}
            title="Total Conversions"
            value={metrics?.totalConversions}
            color="green"
          />
          <MetricCard
            icon={<TrendingUp className="w-6 h-6" />}
            title="Conversion Rate"
            value={`${metrics?.conversionRate}%`}
            color="purple"
          />
          <MetricCard
            icon={<DollarSign className="w-6 h-6" />}
            title="Total Revenue"
            value={`$${metrics?.totalRevenue?.toFixed(2)}`}
            color="yellow"
          />
          <MetricCard
            icon={<Eye className="w-6 h-6" />}
            title="Page Views"
            value={metrics?.pageViews}
            color="pink"
          />
          <MetricCard
            icon={<Clock className="w-6 h-6" />}
            title="Avg Session Duration"
            value={`${metrics?.avgSessionDuration}s`}
            color="indigo"
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Conversion Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Conversion Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={conversionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {conversionData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Top Pages */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Pages</h3>
            <div className="space-y-3">
              {topPages?.slice(0, 5)?.map((page, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">{page?.page_path || 'Unknown'}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white ml-4">{page?.view_count || 0} views</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* User Flow */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top User Flows</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={userFlowData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="flow" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* GA4 Status */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">Google Analytics 4 Integration</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {analyticsService?.isAvailable() 
                  ? 'GA4 is active and tracking events in real-time. View detailed reports in your Google Analytics dashboard.' :'GA4 is not configured. Add your VITE_GOOGLE_ANALYTICS_ID to enable tracking.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ icon, title, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    purple: 'bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400',
    pink: 'bg-pink-100 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400',
    indigo: 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${colorClasses?.[color]}`}>
          {icon}
        </div>
      </div>
      <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{title}</h3>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
    </div>
  );
};

export default AnalyticsDashboard;